import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {CommonModule, NgForOf, NgIf, NgClass} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatTooltipModule} from '@angular/material/tooltip';
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatSelectModule} from "@angular/material/select";
import {MatAutocompleteModule, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {Observable, of, Subject, takeUntil} from "rxjs";
import {QuillEditorComponent} from "ngx-quill";
import Quill from "quill";
import {EditableQuillHtmlDirective} from "../../../../../core/directives/EditableQuillHtmlDirective";
import {DestinationType, ParentType} from "../../destinations.types";
import {CityTypes} from "../../../../auth/auth.types";
import {RestFactoryService} from "../../../../../core/services/rest-factory.service";
import {QuillConfiguration, QuillFormats, setupClipboardInterceptors} from "../../../../../../environments/fixed-enum";
import {OpeningHoursInputComponent} from "../opening-hours-input/opening-hours-input.component";
import {OpeningHoursData, OpeningHoursService} from "../../services/opening-hours.service";
import {PhoneInputComponent} from "../../../../components/phone-input/phone-input.component";
import {MatButtonModule} from "@angular/material/button";
import {SocialLinksEditorComponent} from "../../../../components/social-links-editor/social-links-editor.component";
import {MatCardModule} from "@angular/material/card";
import {environment} from "../../../../../../environments/environment";

@Component({
    selector: 'app-home-tab',
    templateUrl: './home-tab.component.html',
    styleUrls: ['./home-tab.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        MatSelectModule,
        MatAutocompleteModule,
        NgIf,
        NgForOf,
        NgClass,
        QuillEditorComponent,
        EditableQuillHtmlDirective,
        OpeningHoursInputComponent,
        PhoneInputComponent,
        MatButtonModule,
        SocialLinksEditorComponent,
        MatCardModule,
    ],
})
export class HomeTabComponent implements OnInit, OnChanges, OnDestroy {
    @Input() destination: DestinationType;
    @Input() adminMode: boolean = false;
    @Input() reviewMode: boolean = false;
    @Input() destinationTypesArray: any[] = [];

    @Output() save = new EventEmitter<void>();
    @Output() reload = new EventEmitter<any>();
    @Output() parentChange = new EventEmitter<string>();

    @ViewChild('autoCompleteTrigger') autoCompleteTrigger: MatAutocompleteTrigger;

    userUiType: number = environment.user_ui_type || 1;

    parentControl = new FormControl(null);
    filteredParents: Observable<CityTypes[]>;
    parentCheckerRequired: boolean = true;
    typingTimeout: any;

    quillModules: any = QuillConfiguration;
    quillFormats: any[] = QuillFormats;

    openingHours: OpeningHoursData;
    isBusinessHours: boolean = false;
    isSocialNetworks: boolean = false;
    showRawEditorForData1: boolean = false;

    staticDestinationTypes = [
        {
            "en_destinationtype": 4,
            "id_destinationtype": "1c10da20-9db1-45bf-a25e-3e6884a553a6",
            "name": "Hospitality & Accommodation",
            "examples": ["Hotels & Resorts", "Holiday Homes & Apartments", "B&Bs & Guesthouses", "Villas & Luxury Stays", "Experiential Stays"]
        },
        {
            "en_destinationtype": 5,
            "id_destinationtype": "a8843485-6db2-4cdb-b71b-72fe19a3e7ee",
            "name": "Food & Drink",
            "examples": ["Restaurants & Bistros", "Bars & Cocktail Bars", "Cafés & Tea Rooms", "Wine Shops & Tastings", "Street Food", "Food & Wine Experiences"]
        },
        {
            "en_destinationtype": 306,
            "id_destinationtype": "64def2c9-0d96-4e01-9f64-ade4a4fdc0df",
            "name": "Experiences & Entertainment",
            "examples": ["Events & Live Shows", "Music & Performances", "Nightlife & Clubs", "Culture & Art", "Leisure Activities & Attractions", "Experiential Workshops"]
        },
        {
            "en_destinationtype": 714,
            "id_destinationtype": "393090c2-4037-4fcf-a725-9fc9bf40b220",
            "name": "Wellness, Health & Beauty",
            "examples": ["Spas & Thermal Baths", "Wellness Centers & Massage", "Holistic Health", "Beauty & Personal Care", "Fitness & Movement"]
        },
        {
            "en_destinationtype": 7,
            "id_destinationtype": "92f00c0e-5cc0-4501-ac13-0b23e4c543a4",
            "name": "Retail, Artisans & Local Brands",
            "examples": ["Boutiques & Shopping", "Artisans & Handmade", "Local Brands & Design", "Markets & Concept Stores", "Local Products & Souvenirs"]
        },
        {
            "en_destinationtype": 715,
            "id_destinationtype": "dae01c00-42d0-4c26-ae0d-d2e7d27794a1",
            "name": "Tours, Travel & Mobility",
            "examples": ["Tours & Local Guides", "Outdoor & Adventure Experiences", "Travel & Event Planning", "Transportation & Transfers", "Rentals", "VIP & Concierge Services"]
        },
        {
            "en_destinationtype": 719,
            "id_destinationtype": "2f13f82f-a8d9-46d0-a5ac-3cbc0624aa5b",
            "name": "Professional & Personal Services",
            "examples": ["Coaching & Mentoring", "Professional Training & Education", "Consulting", "Legal & Tax Services", "Real Estate Services", "Teaching & Education"]
        }
    ];

    private suppressSave: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private API_GET_DESTINATION = "/admin/destination/search";

    constructor(
        private _restFactoryService: RestFactoryService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _openingHoursService: OpeningHoursService
    ) {
    }

    ngOnInit(): void {
        this.initializeParentSearch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.destination && changes.destination.currentValue) {
            this.suppressSave = true;

            this.parentCheckerRequired = !!this.destination.parent;
            this.initializeParentSearch();
            this.setupData1Editor();

            setTimeout(() => {
                this.suppressSave = false;
            });
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isMissing(val: any): boolean {
        if (val === null || val === undefined) {
            return true;
        }
        if (typeof val === 'string') {
            const stripped = val.replace(/<[^>]*>?/gm, '').trim();
            if (stripped.length === 0 && !val.includes('<img') && !val.includes('<iframe')) {
                return true;
            }
            return false;
        }
        return false;
    }

    isMismatchType(): boolean {
        if (this.userUiType !== 2 && this.userUiType !== 4) return false;
        if (!this.destination || !this.destination.id_destinationtype) return false;
        if (this.destination.id_destinationtype === '2a213080-4272-43b5-b0db-6d35bf1c0bec') return false;

        return !this.staticDestinationTypes.some(t => t.id_destinationtype === this.destination.id_destinationtype);
    }

    getStaticTypeName(id: number): string {
        if (id === 20) return 'Ambassador';
        const staticMatch = this.staticDestinationTypes.find(t => t.en_destinationtype === id);
        if (staticMatch) return staticMatch.name;

        const originalMatch = this.destinationTypesArray?.find(t => t.id_destinationtype === id);
        return originalMatch ? originalMatch.ds_name : '';
    }

    private setupData1Editor(): void {
        const dataLabel1 = this.destination.ds_datalabel1;
        this.isBusinessHours = dataLabel1 === 'Hours';
        this.isSocialNetworks = dataLabel1 === 'SocialNetworks';

        if (this.isBusinessHours) {
            this.showRawEditorForData1 = !this.shouldUseOpeningHoursComponent(this.destination.tx_data1);

            if (!this.showRawEditorForData1) {
                this.openingHours = this._openingHoursService.parseOpeningHoursHtml(this.destination.tx_data1);
            }
        }
    }

    private shouldUseOpeningHoursComponent(html: string): boolean {
        if (!html) {
            return true;
        }
        const trimmed = html.trim();
        return trimmed.startsWith('<ul>') || trimmed.startsWith('<h3>Business Hours:</h3>');
    }

    toggleEditorForData1(): void {
        if (!this.isBusinessHours) {
            return;
        }

        if (!this.showRawEditorForData1) {
            this.destination.tx_data1 = this._openingHoursService.generateOpeningHoursHtmlList(this.openingHours);
        }

        this.showRawEditorForData1 = !this.showRawEditorForData1;

        if (!this.showRawEditorForData1) {
            this.openingHours = this._openingHoursService.parseOpeningHoursHtml(this.destination.tx_data1);
        }

        this._changeDetectorRef.markForCheck();
    }

    handleHoursChange(newData: OpeningHoursData): void {
        this.openingHours = newData;
        this.destination.tx_data1 = this._openingHoursService.generateOpeningHoursHtmlList(newData);
        this.onKeyupAutosave();
    }

    initializeParentSearch(): void {
        if (this.destination && this.destination.parent) {
            this.parentControl.setValue(this.destination.parent);
            this.searchParent(this.destination.parent.ds_name);
        }
    }

    onKeyupAutosave(): void {
        if (this.suppressSave) {
            return;
        }

        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.save.emit();
        }, 500);
    }

    onDestinationTypeChange(event: any): void {
        this.reload.emit(event);
    }

    onEditorCreated(quillInstance: Quill): void {
        setupClipboardInterceptors(quillInstance);
    }

    onBlur(): void {
        setTimeout(() => {
            const value = this.parentControl.value;
            if (typeof value === 'string' || !value) {
                this.parentControl.reset();
                this.parentControl.setErrors({'invalid': true});
            }
            this._changeDetectorRef.markForCheck();
        }, 500);
    }

    displayFn(parent: ParentType): string {
        return parent ? parent.ds_name : '';
    }

    onParentSelected(event: any): void {
        const selectedParentId = event.option.value.id_destination;
        if (selectedParentId !== this.destination.parent?.id_destination) {
            this.parentChange.emit(selectedParentId);
        }
    }

    onKeyupAutoSearch(event: any): void {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            if (event.target.value.length > 2) {
                this.searchParent(event.target.value);
            }
        }, 500);
    }

    onLabelChange(val: string) {
        this.destination.ds_label = val;
        this.destination.ds_title = val;
        this.destination.ds_shortname = val;

        if (this.userUiType === 4) {
            this.destination.ds_name = val;
        }
    }

    searchParent(searchTerm: string): void {
        if (!searchTerm) {
            this.filteredParents = of([]);
            return;
        }

        const data = {
            "ds_name": searchTerm,
            "bl_deleted": false,
            "id_destination": this.destination.id_destination
        };
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);

        this._restFactoryService.httpPostService(this.API_GET_DESTINATION, payload, "ar_results")
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response: CityTypes[]) => {
                this.filteredParents = of(response);
                if (response.length === 0) {
                    this.parentControl.setErrors({'invalid': true});
                }
                this._changeDetectorRef.markForCheck();
            });
    }

    getOriginalTypeName(id: number): string {
        const originalMatch = this.destinationTypesArray?.find(t => t.id_destinationtype === id);
        return originalMatch ? originalMatch.ds_name : `Unknown (ID: ${id})`;
    }
}
